/**
 * Adaptive Cinematic Reveal Engine — top-level config builder.
 *
 * Given an announcement's identifying inputs, returns a fully resolved
 * configuration that the shell + scenes consume.
 */

import { hashSeed, createRng, type SeededRng } from './revealSeed';
import { pickRevealStyle } from './pickRevealStyle';
import {
  getRevealStyle,
  pacingMultiplier,
  type RevealStylePreset,
  type RevealStyleId,
} from './revealStyles';
import { getCinematicPalette, type CinematicPalette } from '../themePalettes';
import { deriveEmotionalSignals, type EmotionalSignals } from './emotionalSignals';
import { pickEmotionalTheme } from './pickEmotionalTheme';
import {
  getEmotionalTheme,
  type EmotionalThemeId,
  type EmotionalThemePreset,
} from './emotionalThemes';
import { pickIntroSequence, type IntroSequence } from './introWordBank';
import { resolveRelationshipProfile, type RelationshipProfile } from './relationshipProfiles';

export interface AdaptiveConfigInput {
  /** Stable identifier — usually announcement.id; falls back to recipient+template hash. */
  seedKey?: string | null;
  animationTheme?: string;
  template?: string;
  recipientName?: string;
  message?: string;
  relationship?: string;
  photoUrls?: string[];
  publishedDate?: string;
}

export interface AdaptiveRevealConfig {
  styleId: RevealStyleId;
  style: RevealStylePreset;
  palette: CinematicPalette;
  /** Stable per-announcement RNG — scenes can pull more random offsets from this. */
  rng: SeededRng;
  /** Multiplier applied to scene base timings (lower = faster). */
  pacing: number;
  /** Buildup duration in ms. */
  buildupMs: number;
  /** Particle density multiplier to apply on top of viewport scale. */
  particleDensity: number;
  /** Optional hue rotation hint for backdrop tint variation. */
  hueShift: number;
  /** Audio bed key (Phase C). */
  audioBedKey: RevealStylePreset['audioBedKey'];
  /** Derived emotional signals — used by scenes for additional variation. */
  signals: EmotionalSignals;
  /** Picked emotional theme — drives lighting cast, particle behavior, micro-motion. */
  emotionalThemeId: EmotionalThemeId;
  emotionalTheme: EmotionalThemePreset;
  /** Picked intro word sequence — drives BuildUpSequence beats. */
  introSequence: IntroSequence;
  /** Held-breath pause before the first intro word, in ms. */
  heldBreathMs: number;
  /** Resolved relationship profile (typography, theme bias, intro flavor). */
  relationshipProfile: RelationshipProfile;
  /** Effective typography preset for the message body. */
  typography: RevealStylePreset['typography'];
}

export function buildAdaptiveConfig(input: AdaptiveConfigInput): AdaptiveRevealConfig {
  const seedSource =
    input.seedKey ??
    `${input.animationTheme ?? ''}|${input.template ?? ''}|${input.recipientName ?? ''}|${input.message?.slice(0, 32) ?? ''}`;
  const seed = hashSeed(seedSource);
  const rng = createRng(seed);

  const signals = deriveEmotionalSignals({
    message: input.message,
    template: input.template,
    relationship: input.relationship,
    photoUrls: input.photoUrls,
    publishedDate: input.publishedDate,
  });

  const styleId = pickRevealStyle({
    rng,
    animationTheme: input.animationTheme,
    messageLength: input.message?.length ?? 0,
  });
  const style = getRevealStyle(styleId);
  const palette = getCinematicPalette(input.animationTheme);

  const relationshipProfile = resolveRelationshipProfile(input.relationship);

  const emotionalThemeId = pickEmotionalTheme(signals, rng, relationshipProfile.themeBias);
  const emotionalTheme = getEmotionalTheme(emotionalThemeId);

  // Splice the relationship's opening beats into the intro pool so the first
  // words of the experience feel specific to who the message is for.
  const relIntroSequences: IntroSequence[] = relationshipProfile.introBeats.map((beats, i) => ({
    id: `rel_${relationshipProfile.key}_${i}`,
    tone: signals.tone,
    beats,
  }));
  const introSequence = relIntroSequences.length && rng.next() < 0.7
    ? rng.pick(relIntroSequences)
    : pickIntroSequence(signals.tone, rng);

  // Phase 3 — slow it down + handcrafted feel.
  // Per-announcement pacing jitter widened to ±14% so two reveals breathe more
  // distinctly. A 1.18x base bump lengthens the reading window so emotion lands
  // before any chrome reappears. Theme + relationship scale on top of that.
  const jitter = 0.86 + rng.next() * 0.28;
  const HANDCRAFTED_BASE = 1.18;
  const pacing =
    pacingMultiplier(style.pacing) *
    jitter *
    HANDCRAFTED_BASE *
    emotionalTheme.pacingScale *
    relationshipProfile.pacingScale;

  const typography = relationshipProfile.typographyOverride ?? emotionalTheme.typography ?? style.typography;

  return {
    styleId,
    style,
    palette,
    rng,
    pacing,
    buildupMs: style.buildupDurationMs,
    particleDensity: style.particleDensity,
    hueShift: (style.hueShift ?? 0) + (emotionalTheme.hue - 40) * 0.15,
    audioBedKey: style.audioBedKey,
    signals,
    emotionalThemeId,
    emotionalTheme,
    introSequence,
    // Extra 1.4s handcrafted "breath" before the first word — keeps the silence
    // between buildup and message reading from feeling mechanical.
    heldBreathMs: emotionalTheme.heldBreathMs + relationshipProfile.extraHeldBreathMs + 1400,
    relationshipProfile,
    typography,
  };
}
